import { useCallback, useEffect, useLayoutEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ScrollToTop from '@/router/ScrollToTop';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { UIProvider } from '@/contexts/UIContext';
import CookieBanner from '@/components/CookieBanner';
import AIAssistant from '@/components/AIAssistant';
import FloatingQuickActions from '@/components/FloatingQuickActions';
import Home from '@/pages/Home';
import TetrisPage from '@/pages/TetrisPage';
import ConsultationPage from '@/pages/ConsultationPage';
import ConsultationThankYouPage from '@/pages/ConsultationThankYouPage';
import PackageSelectionPage from '@/pages/PackageSelectionPage';
import MarketingCostPage from '@/pages/MarketingCostPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import ChecklistPage from '@/pages/ChecklistPage';
import ChecklistThankYouPage from '@/pages/ChecklistThankYouPage';
import About from '@/pages/About';
import ServicesIndex from '@/pages/services/Index';
import WebDesign from '@/pages/services/WebDesign';
import Ecommerce from '@/pages/services/Ecommerce';
import Branding from '@/pages/services/Branding';
import Marketing from '@/pages/services/Marketing';
import SeoService from '@/pages/services/SeoService';
import MobileApp from '@/pages/services/MobileApp';
import ContentProduction from '@/pages/services/ContentProduction';
import Portfolio from '@/pages/Portfolio';
import NotFound from '@/pages/NotFound';
// Archive pages
import PostsArchive from '@/pages/archive/Posts';
import PostDetail from '@/pages/archive/PostDetail';
import KnowledgeArchive from '@/pages/archive/Knowledge';
import GuidesArchive from '@/pages/archive/Guides';
import ToolsArchive from '@/pages/archive/Tools';
import LegislationArchive from '@/pages/archive/Legislation';
import OrganizationsArchive from '@/pages/archive/Organizations';
import ResourcesArchive from '@/pages/archive/Resources';
import FaqsArchive from '@/pages/archive/Faqs';
import { getLanguageFromPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { trackMetaPixelLead } from '@/lib/meta-pixel';
import { useLanguage } from '@/contexts/LanguageContext';

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  useLayoutEffect(() => {
    const pathLanguage = getLanguageFromPath(location.pathname);
    if (pathLanguage !== language) {
      setLanguage(pathLanguage);
    }
  }, [language, location.pathname, setLanguage]);

  const localize = useCallback(
    (path: string) => withLanguagePrefix(path, language),
    [language],
  );

  useEffect(() => {
    window.navigateToTetris = () => navigate(localize('/tetris'));
    window.navigateToHome = () => navigate(localize('/'));
    window.navigateToConsultation = () => {
      trackMetaPixelLead({
        content_name: 'Consultation CTA',
        page_path: location.pathname
      });
      navigate(localize('/consultation'));
    };
    window.navigateToPackageSelection = () => {
      trackMetaPixelLead({
        content_name: 'Package Selection CTA',
        page_path: location.pathname
      });
      navigate(localize('/package-selection'));
    };
    window.navigateToMarketingCost = () => navigate(localize('/marketing-cost'));
    window.navigateToPrivacyPolicy = () => navigate(localize('/privacy-policy'));
    window.navigateToCookiePolicy = () => navigate(localize('/cookie-policy'));
    window.navigateToAbout = () => navigate(localize('/about'));
    window.navigateToWebsiteDevelopment = () => navigate(localize('/services/web-design'));
    window.navigateToEcommerceDevelopment = () => navigate(localize('/services/ecommerce'));
    window.navigateToBranding = () => navigate(localize('/services/branding'));
    window.navigateToMarketingStrategies = () => navigate(localize('/services/marketing-strategy'));
    window.navigateToMobileAppDevelopment = () => navigate(localize('/services/apps'));
    window.navigateToContentProduction = () => navigate(localize('/services/content-production'));
    window.navigateToPortfolio = () => navigate(localize('/projects'));

    return () => {
      window.navigateToTetris = undefined;
      window.navigateToHome = undefined;
      window.navigateToConsultation = undefined;
      window.navigateToPackageSelection = undefined;
      window.navigateToMarketingCost = undefined;
      window.navigateToPrivacyPolicy = undefined;
      window.navigateToCookiePolicy = undefined;
      window.navigateToAbout = undefined;
      window.navigateToWebsiteDevelopment = undefined;
      window.navigateToEcommerceDevelopment = undefined;
      window.navigateToBranding = undefined;
      window.navigateToMarketingStrategies = undefined;
      window.navigateToMobileAppDevelopment = undefined;
      window.navigateToContentProduction = undefined;
      window.navigateToPortfolio = undefined;
    };
  }, [localize, location.pathname, navigate]);

  const normalizedPath = stripLanguagePrefix(location.pathname);
  const showCookieBanner = normalizedPath === '/';
  const hideAIAssistant = normalizedPath === '/tetris';

  const baseRoutes = [
    { path: '/', element: <Home /> },
    { path: '/tetris', element: <TetrisPage /> },
    { path: '/consultation', element: <ConsultationPage /> },
    { path: '/consultation/thank-you', element: <ConsultationThankYouPage /> },
    { path: '/package-selection', element: <PackageSelectionPage /> },
    { path: '/marketing-cost', element: <MarketingCostPage /> },
    { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
    { path: '/cookie-policy', element: <CookiePolicyPage /> },
    { path: '/checklist', element: <ChecklistPage /> },
    { path: '/thank-you/checklist', element: <ChecklistThankYouPage /> },
    { path: '/about', element: <About /> },
    { path: '/services', element: <ServicesIndex /> },
    { path: '/services/web-design', element: <WebDesign /> },
    { path: '/services/ecommerce', element: <Ecommerce /> },
    { path: '/services/branding', element: <Branding /> },
    { path: '/services/marketing-strategy', element: <Marketing /> },
    { path: '/services/seo', element: <SeoService /> },
    { path: '/services/apps', element: <MobileApp /> },
    { path: '/services/content-production', element: <ContentProduction /> },
    { path: '/portfolio', element: <Portfolio /> },
    { path: '/projects', element: <Portfolio /> },
    // Archive routes
    { path: '/blog', element: <PostsArchive /> },
    { path: '/blog/:slug', element: <PostDetail /> },
    { path: '/posts', element: <PostsArchive /> },
    { path: '/posts/:slug', element: <PostDetail /> },
    { path: '/articles', element: <PostsArchive /> },
    { path: '/articles/:slug', element: <PostDetail /> },
    { path: '/resources/articles', element: <PostsArchive /> },
    { path: '/resources/articles/:slug', element: <PostDetail /> },
    { path: '/knowledge', element: <KnowledgeArchive /> },
    { path: '/knowledge/:slug', element: <PostDetail postType="knowledge" /> },
    { path: '/resources/knowledge', element: <KnowledgeArchive /> },
    { path: '/resources/knowledge/:slug', element: <PostDetail postType="knowledge" /> },
    { path: '/guides', element: <GuidesArchive /> },
    { path: '/guides/:slug', element: <PostDetail postType="guides" /> },
    { path: '/resources/guides', element: <GuidesArchive /> },
    { path: '/resources/guides/:slug', element: <PostDetail postType="guides" /> },
    { path: '/tools', element: <ToolsArchive /> },
    { path: '/tools/:slug', element: <PostDetail postType="tools" /> },
    { path: '/resources/tools', element: <ToolsArchive /> },
    { path: '/resources/tools/:slug', element: <PostDetail postType="tools" /> },
    { path: '/regulations', element: <LegislationArchive /> },
    { path: '/regulations/:slug', element: <PostDetail postType="legislation" /> },
    { path: '/resources/regulations', element: <LegislationArchive /> },
    { path: '/resources/regulations/:slug', element: <PostDetail postType="legislation" /> },
    { path: '/legislation', element: <LegislationArchive /> },
    { path: '/legislation/:slug', element: <PostDetail postType="legislation" /> },
    { path: '/organizations', element: <OrganizationsArchive /> },
    { path: '/organizations/:slug', element: <PostDetail postType="organizations" /> },
    { path: '/resources/organizations', element: <OrganizationsArchive /> },
    { path: '/resources/organizations/:slug', element: <PostDetail postType="organizations" /> },
    { path: '/materials', element: <ResourcesArchive /> },
    { path: '/materials/:slug', element: <PostDetail postType="resources" /> },
    { path: '/resources/materials', element: <ResourcesArchive /> },
    { path: '/resources/materials/:slug', element: <PostDetail postType="resources" /> },
    { path: '/resources', element: <ResourcesArchive /> },
    { path: '/resources/:slug', element: <PostDetail postType="resources" /> },
    { path: '/faq', element: <FaqsArchive /> },
    { path: '/faq/:slug', element: <PostDetail postType="faqs" /> },
    { path: '/faqs', element: <FaqsArchive /> },
    { path: '/faqs/:slug', element: <PostDetail postType="faqs" /> },
    { path: '/resources/faq', element: <FaqsArchive /> },
    { path: '/resources/faq/:slug', element: <PostDetail postType="faqs" /> },
  ];

  return (
    <>
      <ScrollToTop />
      <Routes>
        {baseRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        {baseRoutes.map((route) => {
          const enPath = withLanguagePrefix(route.path, 'en');
          return <Route key={enPath} path={enPath} element={route.element} />;
        })}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showCookieBanner && <CookieBanner />}
      {!hideAIAssistant && <AIAssistant />}
      <FloatingQuickActions />
    </>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <UIProvider>
        <AppRoutes />
      </UIProvider>
    </LanguageProvider>
  );
}
